import { useEffect, useRef } from "react";

interface OverlayEntry {
  id: string;
  closedByBack: boolean;
  close: () => void;
}

// 열려 있는 오버레이(시트 · 다이얼로그 · 전체 화면 패널)의 순서.
// 뒤로가기는 가장 위에 열린 것 하나만 닫는다.
const overlayStack: OverlayEntry[] = [];
// UI 로 닫으면서 직접 history.back() 을 호출한 횟수. 그로 인한 popstate 는 무시한다.
let pendingSelfBack = 0;
let listening = false;

function handlePopState() {
  if (pendingSelfBack > 0) {
    pendingSelfBack -= 1;
    return;
  }
  const top = overlayStack.pop();
  if (top === undefined) return;
  top.closedByBack = true;
  top.close();
}

function ensureListener() {
  if (listening) return;
  window.addEventListener("popstate", handlePopState);
  listening = true;
}

/**
 * 오버레이가 열려 있는 동안 브라우저 히스토리에 항목을 하나 쌓아서,
 * 모바일의 뒤로가기(버튼 · 스와이프)가 페이지 이동 대신 오버레이를 닫도록 한다.
 * UI 로 닫힌 경우에는 쌓아둔 항목을 되돌려 히스토리를 원래대로 맞춘다.
 */
export function useBackToClose(open: boolean, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    ensureListener();

    const entry: OverlayEntry = {
      id: `overlay-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      closedByBack: false,
      close: () => onCloseRef.current(),
    };
    overlayStack.push(entry);
    window.history.pushState(
      { ...(window.history.state ?? {}), overlay: entry.id },
      ""
    );

    return () => {
      const index = overlayStack.indexOf(entry);
      if (index !== -1) overlayStack.splice(index, 1);
      // 다른 페이지로 이동하면서 닫힌 경우에는 현재 항목이 우리 것이 아니므로 건드리지 않는다
      if (!entry.closedByBack && window.history.state?.overlay === entry.id) {
        pendingSelfBack += 1;
        window.history.back();
      }
    };
  }, [open]);
}
